import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { Column, GetContextMenuItemsParams, GridApi, IRowNode, MenuItemDef } from '@ag-grid-community/core'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import React, { RefObject, useEffect, useState } from 'react'

interface CopyContextMenuOptions {
  /** Add "Copy Column" / "Copy Column with Header" for the right-clicked column. Default: true. */
  includeColumnCopy?: boolean
  /** Add "Copy Row" / "Copy Row with Header" for the selected row(s). Default: true. */
  includeRowCopy?: boolean
  /** Append ag-grid's default Export submenu after the copy items. Default: false (export lives in the ⋮ menu). */
  includeExport?: boolean
  /** Transform a cell value for Copy Column (e.g. raw instead of formatted) — mirror processCellForClipboard. */
  processCell?: (p: any) => any
}

// Overlays whose clicks shouldn't clear the range (ag-grid popups + MUI menus/dialogs/tooltips).
const OVERLAY_SELECTOR = '.ag-popup, .ag-menu, .MuiModal-root, .MuiTooltip-popper'

/** Clears cell/range selection on click outside the grid (ignores ag-grid + MUI overlays). */
export function useClearCellRangeOnOutsideClick(
  containerRef: RefObject<HTMLElement>,
  getApi: () => GridApi | null | undefined,
) {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (e.button !== 0) return
      const api = getApi()
      if (!api || !containerRef.current) return
      const target = e.target as HTMLElement | null
      if (!target) return
      if (containerRef.current.contains(target)) return
      if (target.closest?.(OVERLAY_SELECTOR)) return
      api.clearRangeSelection?.()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [containerRef, getApi])
}

/** Reveal header sort/filter/menu icons only on column hover. Spread into a grid container's sx. */
export const gridHeaderIconsSx = {
  '& .ag-header-cell .ag-header-icon, & .ag-header-cell .ag-header-cell-menu-button': {
    opacity: 0,
    transition: 'opacity 0.15s ease',
  },
  '& .ag-header-cell:hover .ag-header-icon, & .ag-header-cell:hover .ag-header-cell-menu-button, & .ag-header-cell.ag-column-hover .ag-header-icon, & .ag-header-cell.ag-column-hover .ag-header-cell-menu-button':
    {
      opacity: 1,
    },
}

/** Formatted display value of a row-group column for a node (e.g. "EQTY" -> "Equity"). */
function formatGroupValue(api: GridApi, col: Column, node: IRowNode): string {
  const raw = api.getValue(col, node)
  const colDef = col.getColDef()
  const vf = colDef.valueFormatter
  if (typeof vf === 'function') {
    const out = vf({ value: raw, node, column: col, colDef, context: colDef.context, api, data: node.data })
    return out == null ? '' : String(out)
  }
  return raw == null ? '' : String(raw)
}

/** Tree-data hierarchy path for a node, e.g. "Equity Risk > Region > Pacific > AUD". */
function treePath(node: IRowNode): string {
  const parts: string[] = []
  let cur: IRowNode | null = node
  while (cur && cur.key != null && cur.level >= 0) {
    parts.unshift(String(cur.key))
    cur = cur.parent
  }
  return parts.join(' > ')
}

/** Leading "group" columns for copy — row-group columns, or the tree path for treeData grids. */
function groupLeadColumns(api: GridApi): { header: string; value: (node: IRowNode) => string }[] {
  const groupCols = api.getRowGroupColumns?.() ?? []
  if (groupCols.length) {
    return groupCols.map((c) => ({
      header: String(c.getColDef().headerName ?? c.getColId()),
      value: (node: IRowNode) => formatGroupValue(api, c, node),
    }))
  }
  if (api.getGridOption?.('treeData')) {
    const autoCol = api.getColumn?.('ag-Grid-AutoColumn')
    return [{ header: String(autoCol?.getColDef?.().headerName ?? 'Group'), value: treePath }]
  }
  return []
}

/** Rows covered by the current cell ranges, or the right-clicked node. */
function selectedRowNodes(params: GetContextMenuItemsParams): IRowNode[] {
  const { api } = params
  const ranges = api.getCellRanges?.() ?? []
  const rowNodes: IRowNode[] = []
  const addNode = (n: IRowNode | null | undefined) => { if (n && !rowNodes.includes(n)) rowNodes.push(n) }
  if (ranges.length) {
    ranges.forEach((r) => {
      const a = r.startRow?.rowIndex ?? 0
      const b = r.endRow?.rowIndex ?? 0
      for (let i = Math.min(a, b); i <= Math.max(a, b); i++) addNode(api.getDisplayedRowAtIndex(i))
    })
  } else {
    addNode(params.node)
  }
  return rowNodes
}

/** Copy selected cells with the group hierarchy as leading columns + a header row (tab-separated). */
function copyWithGroupHeaders(params: GetContextMenuItemsParams, processCell?: (p: any) => any) {
  const { api } = params
  const ranges = api.getCellRanges?.() ?? []

  const columns: Column[] = []
  const addCol = (c: Column | null | undefined) => { if (c && !columns.includes(c)) columns.push(c) }
  if (ranges.length) ranges.forEach((r) => r.columns.forEach(addCol))
  else addCol(params.column as Column)

  const groupLead = groupLeadColumns(api)
  const rowNodes = selectedRowNodes(params)

  const valueHeaders = columns.map((c) => String(c.getColDef().headerName ?? c.getColId()))
  const headerLine = [...groupLead.map((g) => g.header), ...valueHeaders].join('\t')

  const dataLines = rowNodes.map((node) => {
    const groupVals = groupLead.map((g) => g.value(node))
    const valueVals = columns.map((c) => {
      const raw = api.getValue(c, node)
      const v = processCell ? processCell({ column: c, node, value: raw, api }) : raw
      return v == null ? '' : String(v)
    })
    return [...groupVals, ...valueVals].join('\t')
  })

  navigator.clipboard?.writeText([headerLine, ...dataLines].join('\n'))
}

/** Copy whole row(s) — group hierarchy as leading columns, then every other displayed column. */
function copyRows(params: GetContextMenuItemsParams, withHeader: boolean, processCell?: (p: any) => any) {
  const { api } = params
  const rowNodes = selectedRowNodes(params)
  const groupLead = groupLeadColumns(api)
  const groupColIds = new Set((api.getRowGroupColumns?.() ?? []).map((c) => c.getColId()))
  const columns = (api.getAllDisplayedColumns?.() ?? []).filter(
    (c) =>
      !c.getColId().startsWith('ag-Group') &&
      !c.getColId().startsWith('ag-Grid-AutoColumn') &&
      !c.getColDef().showRowGroup &&
      !groupColIds.has(c.getColId()),
  )

  const lines: string[] = []
  if (withHeader) {
    const colHeaders = columns.map((c) => String(c.getColDef().headerName ?? c.getColId()))
    lines.push([...groupLead.map((g) => g.header), ...colHeaders].join('\t'))
  }
  rowNodes.forEach((node) => {
    const groupVals = groupLead.map((g) => g.value(node))
    const vals = columns.map((c) => {
      const raw = api.getValue(c, node)
      const v = processCell ? processCell({ column: c, node, value: raw, api }) : raw
      return v == null ? '' : String(v)
    })
    lines.push([...groupVals, ...vals].join('\t'))
  })

  navigator.clipboard?.writeText(lines.join('\n'))
}

export function buildCopyContextMenuItems(options: CopyContextMenuOptions = {}) {
  const { includeColumnCopy = true, includeRowCopy = true, includeExport = false, processCell } = options

  return (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    const items: (string | MenuItemDef)[] = ['copy', 'copyWithHeaders']

    // Group-aware copy — row-grouped or treeData grids only.
    const groupLead = groupLeadColumns(params.api)
    if (groupLead.length > 0) {
      items.push({ name: 'Copy with Group Headers', action: () => copyWithGroupHeaders(params, processCell) })
    }

    const col = params.column
    if (includeColumnCopy && col) {
      const copyColumn = (withHeader: boolean) => {
        // When grouped, prepend the group columns.
        const lines: string[] = []
        if (withHeader) {
          const colHeader = String(col.getColDef().headerName ?? col.getColId())
          lines.push([...groupLead.map((g) => g.header), colHeader].join('\t'))
        }
        params.api.forEachNodeAfterFilterAndSort((node) => {
          if (node.group) return
          const raw = params.api.getValue(col, node)
          const value = processCell ? processCell({ column: col, node, value: raw, api: params.api }) : raw
          const groupVals = groupLead.map((g) => g.value(node))
          lines.push([...groupVals, value == null ? '' : String(value)].join('\t'))
        })
        navigator.clipboard?.writeText(lines.join('\n'))
      }
      items.push('separator')
      items.push({ name: 'Copy Column', action: () => copyColumn(false) })
      items.push({ name: 'Copy Column with Header', action: () => copyColumn(true) })
    }

    if (includeRowCopy) {
      items.push('separator')
      items.push({ name: 'Copy Row', action: () => copyRows(params, false, processCell) })
      items.push({ name: 'Copy Row with Header', action: () => copyRows(params, true, processCell) })
    }

    if (includeExport) items.push('separator', 'export')
    return items
  }
}

export interface GridActionsMenuProps {
  /** Returns the grid's GridApi, e.g. () => gridRef.current?.api */
  getApi: () => GridApi | null | undefined
  /** Base file name for exports (no extension). */
  fileName?: string
  /** Cell value transform for export (e.g. raw full-precision values). */
  processCellCallback?: (params: any) => any
  /** Wraps export execution so a grid can isolate state (e.g. chart view). Defaults to a direct run. */
  wrapRun?: (run: () => void) => void
}

/** Shared 3-dot (⋮) menu for dashboard grids: Export as Excel / CSV. */
export default function GridActionsMenu({
  getApi,
  fileName = 'export',
  processCellCallback,
  wrapRun,
}: GridActionsMenuProps) {
  const theme = useTheme()
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const run = wrapRun ?? ((fn: () => void) => fn())

  const itemSx = {
    fontSize: '14px',
    color: theme.textPrimary,
    '&:hover': { backgroundColor: theme.border },
  }

  const handleExport = (type: 'CSV' | 'EXCEL') => {
    const api = getApi()
    if (api) {
      run(() => {
        if (type === 'EXCEL') api.exportDataAsExcel({ fileName, processCellCallback })
        else api.exportDataAsCsv({ fileName: `${fileName}.csv`, processCellCallback })
      })
    }
    setAnchor(null)
  }

  return (
    <>
      <IconButton
        size='small'
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          borderRadius: '6px',
          padding: '5px',
          backgroundColor: theme.cardBackground,
          border: `1px solid ${theme.border}`,
          color: theme.textPrimary,
          '&:hover': { backgroundColor: theme.border },
        }}
      >
        <MoreVertIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{
          sx: {
            backgroundColor: theme.cardBackground,
            border: `1px solid ${theme.border}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            minWidth: 140,
          },
        }}
      >
        <MenuItem onClick={() => handleExport('EXCEL')} sx={itemSx}>
          Export as Excel
        </MenuItem>
        <MenuItem onClick={() => handleExport('CSV')} sx={itemSx}>
          Export as CSV
        </MenuItem>
      </Menu>
    </>
  )
}
